import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Text, Checkbox, TextInput, Button, Group } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AuthDebugInfo } from './AuthDebugInfo';
import ChatWidget from './chat/ChatWidget';
import { useActualUserIsImperator } from '../hooks/useIsImperator';
import { debugService } from '../services/DebugService';
import { enableDebugLogging, disableDebugLogging, isDebugLoggingEnabled } from '../utils/debugLog';
import BuildTimeService from '../services/BuildTimeService';
import ImpersonationService from '../services/ImpersonationService';
import EntitlementsService from '../services/EntitlementsService';
import MeService from '../services/MeService';
import { OnBehalfOfStorageDialog } from './dialogs';

interface FooterProps {
    navOpen?: boolean;
    onImpersonationChange?: () => void;
}

export const Footer: React.FC<FooterProps> = ({ navOpen = false, onImpersonationChange }) => {
    const buildId = process.env.REACT_APP_BUILD_ID || 'dev';
    const buildNumber = process.env.REACT_APP_VERSION || 'dev';
    const buildDate = process.env.REACT_APP_BUILD_DATE || '';
    const environment = process.env.REACT_APP_ENV || 'development';
    const gitCommit = process.env.REACT_APP_GIT_COMMIT || '';

    const isImperator = useActualUserIsImperator(true);

    const [onBehalfOfEnabled, setOnBehalfOfEnabled] = useState(() =>
        ImpersonationService.getPersistedOnBehalfOfEnabled());
    const [onBehalfOfUpn, setOnBehalfOfUpn] = useState(() => ImpersonationService.getPersistedOnBehalfOfUpn());
    const onBehalfOfEnabledRef = useRef(onBehalfOfEnabled);
    const [storageDialogOpen, setStorageDialogOpen] = useState(false);
    const [recentUpnsCount, setRecentUpnsCount] = useState(() => ImpersonationService.getRecentUpns().length);

    useEffect(() => { onBehalfOfEnabledRef.current = onBehalfOfEnabled; }, [onBehalfOfEnabled]);

    useEffect(() => {
        const sync = () => setRecentUpnsCount(ImpersonationService.getRecentUpns().length);
        sync();
        return ImpersonationService.subscribeRecentUpns(sync);
    }, []);

    useEffect(() => {
        const syncState = () => {
            const enabled = ImpersonationService.getOnBehalfOfEnabled();
            const upn = ImpersonationService.getOnBehalfOfUpn();
            setOnBehalfOfEnabled(enabled);
            setOnBehalfOfUpn(upn);
        };
        syncState(); // run once so service state (if restored below) is reflected
        return ImpersonationService.subscribeOnBehalfOf(syncState);
    }, []);

    /** On load: sync Footer with session OBO; re-validate only if not already confirmed for this tab. */
    useEffect(() => {
        const persistedEnabled = ImpersonationService.getPersistedOnBehalfOfEnabled();
        const persistedUpn = ImpersonationService.getPersistedOnBehalfOfUpn().trim();
        if (!persistedEnabled || !persistedUpn) return;
        ImpersonationService.setOnBehalfOf(true, persistedUpn);
        if (ImpersonationService.getIsOnBehalfOfEffective()) {
            EntitlementsService.fetchEntityEntitlements(true).catch(() => {});
            return;
        }
        let cancelled = false;
        const run = async () => {
            ImpersonationService.setValidationError(null);
            ImpersonationService.setPendingUpn(persistedUpn);
            try {
                const data = await MeService.fetchMe();
                if (cancelled) return;
                if (data) {
                    ImpersonationService.setOnBehalfOfConfirmed(persistedUpn);
                    ImpersonationService.setValidationError(null);
                    EntitlementsService.fetchEntityEntitlements(true).catch(() => {});
                    // Do not call onImpersonationChange here: that navigates to dashboard. On reload we should keep the current route (e.g. #groups).
                } else {
                    ImpersonationService.clearOnBehalfOfPending();
                    ImpersonationService.setValidationError('No data returned for this user.');
                }
            } catch {
                if (cancelled) return;
                ImpersonationService.clearOnBehalfOfPending();
                const fetchErr = MeService.getLastFetchError();
                ImpersonationService.setValidationError(fetchErr?.message ?? 'Unable to validate the specified user.');
            }
        };
        run();
        return () => { cancelled = true; };
    }, []);

    /**
     * Central validation: sets pending UPN, calls entities/me, handles success and all error variants.
     * On success triggers entitlements refresh and notifies parent to refresh the page.
     */
    const validateAndActivate = useCallback(async (upn: string) => {
        const trimmed = upn.trim();
        if (!trimmed) return;

        ImpersonationService.setValidationError(null);
        ImpersonationService.setPendingUpn(trimmed);

        try {
            const data = await MeService.fetchMe();
            if (data) {
                ImpersonationService.setOnBehalfOfConfirmed(trimmed);
                ImpersonationService.setValidationError(null);
                EntitlementsService.fetchEntityEntitlements(true).catch(() => {});
                notifications.show({
                    title: 'Impersonation Active',
                    message: `Now viewing as ${data.DisplayName || trimmed}`,
                    color: 'yellow',
                    autoClose: 4000,
                });
                onImpersonationChange?.();
            } else {
                ImpersonationService.clearOnBehalfOfPending();
                const msg = 'No data returned for this user.';
                ImpersonationService.setValidationError(msg);
                notifications.show({ title: 'Impersonation Failed', message: msg, color: 'red', autoClose: 6000 });
            }
        } catch {
            ImpersonationService.clearOnBehalfOfPending();
            const fetchErr = MeService.getLastFetchError();
            const msg = fetchErr?.message || 'Unable to validate the specified user.';
            ImpersonationService.setValidationError(msg);
            notifications.show({
                title: fetchErr?.isAdminOnAdmin ? 'Not Allowed' : 'Impersonation Failed',
                message: msg,
                color: 'red',
                autoClose: 8000,
            });
        }
    }, [onImpersonationChange]);

    const [debugEnabled, setDebugEnabled] = React.useState(debugService.isEnabled());

    React.useEffect(() => {
        const unsubscribe = debugService.subscribe(() => {
            const enabled = debugService.isEnabled();
            setDebugEnabled(enabled);
            if (enabled) {
                enableDebugLogging();
            } else {
                disableDebugLogging();
            }
        });
        if (debugService.isEnabled()) {
            enableDebugLogging();
        } else {
            disableDebugLogging();
        }
        return unsubscribe;
    }, []);

    const [buildTimeData, setBuildTimeData] = React.useState<any>(null);
    const [buildTimeLoading, setBuildTimeLoading] = React.useState(false);
    const [buildTimeError, setBuildTimeError] = React.useState<string | null>(null);

    React.useEffect(() => {
        const unsubscribe = BuildTimeService.subscribe(() => {
            setBuildTimeData(BuildTimeService.getBuildTime());
            setBuildTimeLoading(BuildTimeService.isLoading());
            setBuildTimeError(BuildTimeService.getError());
        });
        return unsubscribe;
    }, []);

    React.useEffect(() => {
        const cached = BuildTimeService.getBuildTime();
        if (cached) {
            setBuildTimeData(cached);
            setBuildTimeLoading(false);
        } else {
            BuildTimeService.fetchBuildTime().then((data) => {
                if (data) {
                    setBuildTimeData(data);
                }
            });
        }
    }, []);

    const formatBuildTime = (buildTime: string | Date | any): string => {
        try {
            const date = typeof buildTime === 'string' ? new Date(buildTime) : new Date(buildTime);
            if (isNaN(date.getTime())) {
                return String(buildTime);
            }
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        } catch {
            return String(buildTime);
        }
    };

    const getBuildDateInfo = () => {
        if (!buildDate) return null;
        try {
            const date = new Date(buildDate);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hour = String(date.getHours()).padStart(2, '0');
            const minute = String(date.getMinutes()).padStart(2, '0');
            return { date: `${year}-${month}-${day}`, hour, minute };
        } catch {
            return null;
        }
    };

    const buildDateInfo = getBuildDateInfo();

    const applyStoredOnBehalfOfUpn = (upn: string) => {
        const trimmed = upn.trim();
        if (!trimmed) return;

        setOnBehalfOfEnabled(true);
        setOnBehalfOfUpn(trimmed);
        ImpersonationService.setOnBehalfOf(true, trimmed);
        validateAndActivate(trimmed);
    };

    return (
        <Box
            component="footer"
            style={{
                position: 'sticky',
                bottom: 0,
                zIndex: 160,
                padding: '8px 16px',
                marginLeft: navOpen ? 200 : 0,
                transition: 'margin-left 0.2s ease-in-out',
                borderTop: '1px solid var(--mantine-color-default-border)',
                background: 'var(--mantine-color-body)',
                minHeight: 40,
            }}
        >
            <Group justify="space-between" align="center" wrap="wrap">
                <Group gap="md" align="center">
                    <Text size="sm" fw={500}>Hades</Text>
                    {buildTimeData && buildTimeData.BuildTime && (
                        <Text
                            size="xs"
                            c="dimmed"
                            ff="monospace"
                            opacity={0.7}
                            title={`Backend Build Time: ${buildTimeData.BuildTime}`}
                        >
                            Build: {formatBuildTime(buildTimeData.BuildTime)}
                        </Text>
                    )}
                    {buildTimeError && (
                        <Text size="xs" c="red" ff="monospace">BuildTime: {buildTimeError}</Text>
                    )}
                    {buildTimeLoading && !buildTimeData && !buildTimeError && (
                        <Text size="xs" c="dimmed" ff="monospace">Loading build time...</Text>
                    )}
                </Group>
                <Group gap="sm" align="center" wrap="wrap">
                    {(isImperator === true || isImperator === null) && (
                        <>
                            <Checkbox
                                size="xs"
                                label="On behalf of"
                                checked={onBehalfOfEnabled}
                                onChange={(e) => {
                                    const checked = e.currentTarget.checked;
                                    setOnBehalfOfEnabled(checked);
                                    ImpersonationService.setOnBehalfOf(checked, onBehalfOfUpn);
                                    if (checked && onBehalfOfUpn.trim()) {
                                        validateAndActivate(onBehalfOfUpn.trim());
                                    } else if (!checked) {
                                        ImpersonationService.setValidationError(null);
                                        MeService.clearMe();
                                        EntitlementsService.fetchEntityEntitlements(true).catch(() => {});
                                        MeService.fetchMe().catch(() => {});
                                        onImpersonationChange?.();
                                    }
                                }}
                            />
                            {onBehalfOfEnabled && (
                                <TextInput
                                    size="xs"
                                    placeholder="UPN then press Enter"
                                    value={onBehalfOfUpn}
                                    onChange={(e) => {
                                        setOnBehalfOfUpn(e.currentTarget.value);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            const upn = onBehalfOfUpn.trim();
                                            ImpersonationService.setOnBehalfOf(onBehalfOfEnabledRef.current, onBehalfOfUpn);
                                            if (upn) {
                                                validateAndActivate(upn);
                                            }
                                        }
                                    }}
                                    style={{ width: 220 }}
                                    aria-label="On behalf of UPN"
                                />
                            )}
                            {recentUpnsCount > 1 && (
                                <>
                                    <Button
                                        size="xs"
                                        variant="subtle"
                                        onClick={() => setStorageDialogOpen(true)}
                                    >
                                        Storage
                                    </Button>
                                    <OnBehalfOfStorageDialog
                                        open={storageDialogOpen}
                                        onClose={() => setStorageDialogOpen(false)}
                                        onSelectUpn={applyStoredOnBehalfOfUpn}
                                    />
                                </>
                            )}
                            <Checkbox
                                size="xs"
                                label="Debug"
                                checked={debugEnabled}
                                onChange={(e) => {
                                    if (e.currentTarget.checked) {
                                        debugService.enable();
                                        enableDebugLogging();
                                    } else {
                                        debugService.disable();
                                        disableDebugLogging();
                                    }
                                }}
                            />
                        </>
                    )}
                    <Text
                        size="xs"
                        c="dimmed"
                        ff="monospace"
                        title={gitCommit ? `Frontend | Git: ${gitCommit}` : 'Frontend'}
                    >
                        Build: {buildNumber} | ID: {buildId} | Env: {environment}
                        {gitCommit && <> | {gitCommit.length >= 7 ? gitCommit.slice(0, 7) : gitCommit}</>}
                        {buildDateInfo && (
                            <> | {buildDateInfo.date} {buildDateInfo.hour}:{buildDateInfo.minute}</>
                        )}
                    </Text>
                    <AuthDebugInfo />
                    <ChatWidget />
                </Group>
            </Group>
        </Box>
    );
};
